import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetFAQsQuery, useLazyGetSearchResultsQuery } from '../../features/resourcesSlice';
import { useSelector } from 'react-redux';
import { useAuthProfile } from '../../app/hooks';
import { MarkedDown } from '../resources/ResourceResults';
import type { RootState } from '../../app/store';

const scrollContainerSx = {
  maxHeight: 300,
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(101, 110, 255, 0.3)',
    borderRadius: 2,
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(101, 110, 255, 0.3) transparent',
};

export const OnboardingResourcePreview: React.FC = () => {
  const { data: faqResult, isLoading, isSuccess } = useGetFAQsQuery();
  const { profile, isSuccess: isAuthSuccess } = useAuthProfile();
  const isDataAboutToArrive = useSelector((state: RootState) => state.lazyResouceCompanion.isDataAboutToArrive);
  const [getSearch, { data: searchResult, isSuccess: isSearchSuccess, isError: isSearchError }] = useLazyGetSearchResultsQuery();

  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  const glassySx = {
    borderRadius: 3,
    backgroundColor: 'rgba(244, 240, 255, 0.55)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: 'none',
    boxShadow: '0 2px 12px rgba(80, 60, 160, 0.06), inset 0 0 0 1px rgba(164, 142, 233, 0.18)',
    overflow: 'hidden',
  };

  const handleQuestionClick = (question: string) => {
    setSelectedQuestion(question);
    if (isAuthSuccess && profile?.id) {
      getSearch({ query: question, profile_id: profile.id }, true);
    }
  };

  if (isLoading || !isSuccess || !faqResult?.length) {
    return (
      <Box sx={{ ...glassySx, p: 2.5 }}>
        <Stack gap={1.5}>
          <Skeleton animation="wave" variant="rounded" width="50%" height={20} />
          <Skeleton animation="wave" variant="rounded" width="100%" height={40} />
          <Skeleton animation="wave" variant="rounded" width="100%" height={40} />
          <Skeleton animation="wave" variant="rounded" width="100%" height={40} />
        </Stack>
      </Box>
    );
  }

  const firstCategory = faqResult[0];
  const questions: string[] = firstCategory.questions ?? [];
  const maxQuestions = 5;

  // ---- Answer view ----
  if (selectedQuestion) {
    return (
      <Box sx={glassySx}>
        {/* Header with back button */}
        <Stack direction="row" alignItems="center" gap={1} sx={{
          background: 'linear-gradient(135deg, rgba(139,92,246,0.85) 0%, rgba(165,120,255,0.80) 100%)',
          px: 1.5, py: 1,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
        }}>
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => setSelectedQuestion(null)}
            sx={{
              color: 'white', textTransform: 'none', fontWeight: 600,
              fontSize: '0.8rem', minWidth: 0, px: 1, py: 0.25,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
            }}
          >
            Back
          </Button>
        </Stack>

        {/* Question title */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', lineHeight: 1.4 }}>
            {selectedQuestion}
          </Typography>
        </Box>

        {/* Streamed answer */}
        <Box sx={{ px: 2, pb: 2, ...scrollContainerSx }}>
          {isSearchError && (
            <Alert severity="error" sx={{ fontSize: 12 }}>
              Sorry, an error occurred. Please try again later.
            </Alert>
          )}
          {isDataAboutToArrive && isSearchSuccess ? (
            <Box sx={{
              '& h1, & h2, & h3': { fontSize: '0.85rem', fontWeight: 700, color: '#2a2a3e', mt: 1.5, mb: 0.5 },
              '& p': { fontSize: 12, lineHeight: 1.6, color: '#444' },
              '& li': { fontSize: 12, lineHeight: 1.6, color: '#444' },
              '& a': { color: '#656EFF', textDecoration: 'none', fontSize: 12 },
            }}>
              <MarkedDown
                searchText={{ cleanText: selectedQuestion, validateText: selectedQuestion }}
                searchResult={searchResult}
                compact
              />
            </Box>
          ) : (
            <Stack gap={1} sx={{ pt: 0.5 }}>
              <Skeleton animation="wave" variant="rounded" width="80%" height={14} />
              <Skeleton animation="wave" variant="rounded" width="100%" height={14} />
              <Skeleton animation="wave" variant="rounded" width="60%" height={14} />
              <Skeleton animation="wave" variant="rounded" width="90%" height={14} />
              <Skeleton animation="wave" variant="rounded" width="70%" height={14} />
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  // ---- FAQ list view ----
  return (
    <Box sx={glassySx}>
      {/* Category header */}
      <Stack direction="row" alignItems="center" gap={1} sx={{
        background: 'linear-gradient(135deg, rgba(139,92,246,0.85) 0%, rgba(165,120,255,0.80) 100%)',
        px: 2, py: 1.25,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
      }}>
        <Box sx={{
          minWidth: 28, height: 28, borderRadius: '8px',
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <MenuBookIcon sx={{ fontSize: 15, color: 'white' }} />
        </Box>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3 }}>
          {firstCategory.category} Patient Guide
        </Typography>
      </Stack>

      {/* Questions list */}
      <Stack direction="column" sx={scrollContainerSx}>
        {questions.slice(0, maxQuestions).map((q, idx) => (
          <React.Fragment key={idx}>
            <Box
              onClick={() => handleQuestionClick(q)}
              sx={{
                px: 2, py: 1.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                backgroundColor: idx % 2 === 1 ? 'rgba(101, 110, 255, 0.025)' : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(101, 110, 255, 0.06)',
                },
                '&:hover .faq-arrow': {
                  color: '#656EFF',
                  transform: 'translate(1px, -1px)',
                },
              }}
            >
              <Typography sx={{
                fontSize: 13, fontWeight: 500, color: '#2a2a3e', lineHeight: 1.4,
                flex: 1, minWidth: 0,
              }}>
                {q}
              </Typography>
              <ArrowOutwardIcon className="faq-arrow" sx={{ color: '#aaa', fontSize: 16, transition: 'all 0.15s ease', flexShrink: 0 }} />
            </Box>
            {idx < Math.min(questions.length, maxQuestions) - 1 && (
              <Divider sx={{ mx: 'auto', width: '92%', borderColor: 'rgba(164, 142, 233, 0.12)' }} />
            )}
          </React.Fragment>
        ))}
      </Stack>

      {/* Footer hint */}
      {questions.length > maxQuestions && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(164, 142, 233, 0.12)', backgroundColor: 'rgba(247, 246, 255, 0.5)' }}>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>
            + {questions.length - maxQuestions} more questions available
          </Typography>
        </Box>
      )}
    </Box>
  );
};
